"use client"

import { useEffect, useRef, useState } from 'react'
import {
  DEFAULT_BACKGROUND_PATTERN_SETTINGS,
  type BandBoundaryNoiseType,
  type DitherPattern,
  type DotShape,
  type NoiseSource,
  type TrailingActorRiskConfidenceStop,
} from './backgroundPatternStore'
import {
  useBackgroundPatternSettings,
  useResetBackgroundPatternSettings,
  useSetBackgroundPatternSettings,
} from './BackgroundPatternSettingsProvider'

const NOISE_SOURCE_OPTIONS: { value: NoiseSource; label: string }[] = [
  { value: 'none', label: 'None (no noise)' },
  { value: 'perlinNoise', label: 'Perlin noise' },
  { value: 'turbulence', label: 'Turbulence' },
]

const DITHER_PATTERN_OPTIONS: { value: DitherPattern; label: string }[] = [
  { value: 'none', label: 'None (smooth)' },
  { value: 'checkerboard', label: 'Checkerboard' },
  { value: 'bayer4', label: 'Bayer 4×4' },
  { value: 'bayer8', label: 'Bayer 8×8' },
  { value: 'horizontalLines', label: 'Horizontal lines' },
  { value: 'verticalLines', label: 'Vertical lines' },
  { value: 'diagonalLines', label: 'Diagonal lines' },
  { value: 'dots', label: 'Dots (halftone)' },
]

const DOT_SHAPE_OPTIONS: { value: DotShape; label: string }[] = [
  { value: 'circle', label: 'Circle' },
  { value: 'square', label: 'Square' },
]

const BAND_BOUNDARY_NOISE_TYPE_OPTIONS: { value: BandBoundaryNoiseType; label: string }[] = [
  { value: 'value', label: 'Value (smooth)' },
  { value: 'turbulence', label: 'Turbulence (ridged)' },
]

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="flex flex-col gap-0.5 font-mono text-[10px] text-black/70">
    <span className="text-[9px] uppercase tracking-wider text-black/50">{label}</span>
    {children}
  </label>
)

const NumberRow = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format = (n) => n.toFixed(2),
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (next: number) => void
  format?: (n: number) => string
}) => (
  <Field label={label}>
    <div className="flex items-center gap-1.5">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => {
          const parsed = parseFloat(event.target.value)
          if (Number.isFinite(parsed)) onChange(parsed)
        }}
        className="flex-1 min-w-0"
      />
      <span className="w-[52px] text-right tabular-nums text-[9px]">
        {format(value)}
      </span>
    </div>
  </Field>
)

const ColorRow = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (next: string) => void
}) => (
  <Field label={label}>
    <div className="flex items-center gap-1.5">
      <input
        type="color"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-7 h-6 p-0 border border-black/10 bg-transparent cursor-pointer"
        aria-label={label}
      />
      <input
        type="text"
        value={value}
        onChange={(event) => {
          const candidate = event.target.value.trim()
          if (/^#[0-9a-fA-F]{6}$/.test(candidate)) onChange(candidate)
        }}
        className="flex-1 min-w-0 bg-transparent border border-black/10 rounded px-1 py-0.5 font-mono text-[10px]"
        spellCheck={false}
      />
    </div>
  </Field>
)

// Piecewise-linear confidence → dither-density map editor. Each stop is a point
// on the preview curve (drag to set confidence=x / density=y) with matching
// number inputs; the field interpolates linearly between them. Stops stay in
// author order (no live re-sort); the preview sorts a copy for the curve.
const ConfidenceStopsEditor = ({
  stops,
  onChange,
}: {
  stops: TrailingActorRiskConfidenceStop[]
  onChange: (next: TrailingActorRiskConfidenceStop[]) => void
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const patchStop = (index: number, patch: Partial<TrailingActorRiskConfidenceStop>) =>
    onChange(stops.map((stop, i) => (i === index ? { ...stop, ...patch } : stop)))
  const removeStop = (index: number) => {
    if (stops.length <= 2) return
    onChange(stops.filter((_, i) => i !== index))
  }
  const addStop = () => onChange([...stops, { confidence: 0.5, density: 0.5 }])

  const previewWidth = 226
  const previewHeight = 90
  const pad = 7
  const innerW = previewWidth - pad * 2
  const innerH = previewHeight - pad * 2
  const toX = (confidence: number) => pad + confidence * innerW
  const toY = (density: number) => pad + (1 - density) * innerH
  // Keep the original index so a dragged/sorted point patches the right stop.
  const sorted = stops
    .map((stop, index) => ({ ...stop, index }))
    .sort((a, b) => a.confidence - b.confidence)
  const curvePoints = sorted
    .map((stop) => `${toX(stop.confidence).toFixed(1)},${toY(stop.density).toFixed(1)}`)
    .join(' ')

  // Drag maps cursor position within the preview to (confidence, density).
  // Latest stops/onChange read via a ref so the window listeners don't need to
  // resubscribe on every move.
  const dragStateRef = useRef({ stops, onChange, innerW, innerH, pad })
  dragStateRef.current = { stops, onChange, innerW, innerH, pad }
  useEffect(() => {
    if (draggingIndex === null) return
    const onMove = (event: PointerEvent) => {
      const svg = svgRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const state = dragStateRef.current
      const round = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 1000) / 1000
      const confidence = round((event.clientX - rect.left - state.pad) / state.innerW)
      const density = round(1 - (event.clientY - rect.top - state.pad) / state.innerH)
      state.onChange(
        state.stops.map((stop, i) =>
          i === draggingIndex ? { ...stop, confidence, density } : stop),
      )
    }
    const onUp = () => setDraggingIndex(null)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [draggingIndex])

  return (
    <Field label="Confidence → density (drag points)">
      <svg
        ref={svgRef}
        width={previewWidth}
        height={previewHeight}
        className="border border-black/10 bg-white/60 touch-none select-none"
      >
        <polyline points={curvePoints} fill="none" stroke="#111" strokeWidth={1.25} />
        {sorted.map((stop) => (
          <circle
            key={stop.index}
            cx={toX(stop.confidence)}
            cy={toY(stop.density)}
            r={draggingIndex === stop.index ? 5 : 4}
            className="cursor-grab fill-[#111] hover:fill-[#c0392b] active:cursor-grabbing"
            onPointerDown={(event) => {
              event.preventDefault()
              setDraggingIndex(stop.index)
            }}
          />
        ))}
      </svg>
      <div className="mt-0.5 flex flex-col gap-0.5">
        {stops.map((stop, index) => (
          <div key={index} className="flex items-center gap-1 font-mono text-[10px]">
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={stop.confidence}
              onChange={(event) => {
                const value = parseFloat(event.target.value)
                if (Number.isFinite(value)) patchStop(index, { confidence: value })
              }}
              className="w-14 bg-transparent border border-black/10 rounded px-1 py-0.5 text-right"
            />
            <span className="text-black/40">→</span>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={stop.density}
              onChange={(event) => {
                const value = parseFloat(event.target.value)
                if (Number.isFinite(value)) patchStop(index, { density: value })
              }}
              className="w-14 bg-transparent border border-black/10 rounded px-1 py-0.5 text-right"
            />
            <button
              type="button"
              onClick={() => removeStop(index)}
              disabled={stops.length <= 2}
              className="ml-auto px-1 leading-none text-black/40 hover:text-black/80 disabled:opacity-30"
              aria-label="remove stop"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addStop}
        className="mt-0.5 self-start rounded border border-black/15 px-1.5 py-0.5 font-mono text-[10px] text-black/70 hover:bg-black/5"
      >
        + add stop
      </button>
    </Field>
  )
}

// Persists each section's open/closed state across mounts so expanding a
// section doesn't reset every time the user toggles the panel with ⌘+G.
// Tracks the *expanded* set rather than the collapsed set so the default
// (no localStorage entry) means every section starts collapsed.
const EXPANDED_SECTIONS_STORAGE_KEY = 'bgPatternControlsExpandedSections'

const readExpandedSections = (): Set<string> => {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(EXPANDED_SECTIONS_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((entry): entry is string => typeof entry === 'string'))
  } catch {
    return new Set()
  }
}

const writeExpandedSections = (expanded: Set<string>): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      EXPANDED_SECTIONS_STORAGE_KEY,
      JSON.stringify(Array.from(expanded)),
    )
  } catch {
    // localStorage may be unavailable (private mode, quota); silently
    // fall back to in-memory-only state for this session.
  }
}

const CollapsibleSection = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    return readExpandedSections().has(title)
  })

  const toggle = () => {
    setIsOpen((wasOpen) => {
      const nextOpen = !wasOpen
      const expanded = readExpandedSections()
      if (nextOpen) expanded.add(title)
      else expanded.delete(title)
      writeExpandedSections(expanded)
      return nextOpen
    })
  }

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={toggle}
        className="flex items-center justify-between gap-2 font-mono text-[9px] uppercase tracking-wider text-black/50 hover:text-black/80"
      >
        <span>{title}</span>
        <span>{isOpen ? '▾' : '▸'}</span>
      </button>
      {isOpen && <div className="flex flex-col gap-2">{children}</div>}
    </div>
  )
}

export const BackgroundPatternControls = ({
  panelClassName = 'fixed bottom-4 left-4 z-[9999]',
  isAgentPopulationHidden = false,
  onAgentPopulationHiddenChange,
  isPolicyGraphHidden = false,
  onPolicyGraphHiddenChange,
}: {
  panelClassName?: string
  isAgentPopulationHidden?: boolean
  onAgentPopulationHiddenChange?: (isHidden: boolean) => void
  isPolicyGraphHidden?: boolean
  onPolicyGraphHiddenChange?: (isHidden: boolean) => void
}) => {
  // Hidden by default. Toggle with ⌘+G / Ctrl+G so the panel doesn't clutter
  // the page during normal viewing.
  const [isPanelMounted, setIsPanelMounted] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const isModifierActive = event.metaKey || event.ctrlKey
      if (!isModifierActive) return
      if (event.key.toLowerCase() !== 'g') return
      event.preventDefault()
      setIsPanelMounted((wasMounted) => {
        // When opening the panel from a hidden state, also expand it so the
        // controls are immediately usable; subsequent toggles just show/hide.
        if (!wasMounted) setIsOpen(true)
        return !wasMounted
      })
    }
    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [])
  const settings = useBackgroundPatternSettings()
  const setBackgroundPatternSettings = useSetBackgroundPatternSettings()
  const handleResetToDefaults = useResetBackgroundPatternSettings()

  const showNoiseControls = settings.noiseSource !== 'none'
  const showCellSizeControl = settings.ditherPattern !== 'none'
  if (!isPanelMounted) return null
  return (
    <div
      className={`${panelClassName} bg-white/95 backdrop-blur-sm border border-black/10 rounded shadow-lg select-none`}
      style={{ width: isOpen ? 260 : undefined }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((previousOpen) => !previousOpen)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-black/70"
      >
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm border border-black/20"
            style={{ backgroundColor: settings.color, opacity: settings.opacity }}
          />
          <span>Race BG pattern</span>
        </span>
        <span className="text-[9px]">{isOpen ? '▾' : '▸'}</span>
      </button>
      {isOpen && (
        <div className="px-2 pb-2 flex flex-col gap-2.5 border-t border-black/10 pt-2">
          <div className="flex flex-col gap-2.5">
          {(onAgentPopulationHiddenChange || onPolicyGraphHiddenChange) && (
            <CollapsibleSection title="HUD">
              {onAgentPopulationHiddenChange && (
                <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
                  <input
                    type="checkbox"
                    checked={isAgentPopulationHidden}
                    onChange={(event) =>
                      onAgentPopulationHiddenChange(event.target.checked)
                    }
                  />
                  <span>Hide AI copies row</span>
                </label>
              )}
              {onPolicyGraphHiddenChange && (
                <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
                  <input
                    type="checkbox"
                    checked={isPolicyGraphHidden}
                    onChange={(event) =>
                      onPolicyGraphHiddenChange(event.target.checked)
                    }
                  />
                  <span>Hide policy graph</span>
                </label>
              )}
            </CollapsibleSection>
          )}
          <CollapsibleSection title="Overlay">
            <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(event) =>
                  setBackgroundPatternSettings({ enabled: event.target.checked })
                }
              />
              <span>Enable overlay</span>
            </label>
            <Field label="Color">
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={settings.color}
                  onChange={(event) =>
                    setBackgroundPatternSettings({ color: event.target.value })
                  }
                  className="w-7 h-6 p-0 border border-black/10 bg-transparent cursor-pointer"
                  aria-label="Pattern color"
                />
                <input
                  type="text"
                  value={settings.color}
                  onChange={(event) => {
                    const candidate = event.target.value.trim()
                    if (/^#[0-9a-fA-F]{6}$/.test(candidate)) {
                      setBackgroundPatternSettings({ color: candidate })
                    }
                  }}
                  className="flex-1 min-w-0 bg-transparent border border-black/10 rounded px-1 py-0.5 font-mono text-[10px]"
                  spellCheck={false}
                />
              </div>
            </Field>
            <NumberRow
              label="Opacity"
              value={settings.opacity}
              min={0}
              max={1}
              step={0.01}
              onChange={(value) => setBackgroundPatternSettings({ opacity: value })}
            />
            <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
              <input
                type="checkbox"
                checked={settings.showTrailingActorRisk}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    showTrailingActorRisk: event.target.checked,
                  })
                }
              />
              <span>Show trailing actor risk</span>
            </label>
            {settings.showTrailingActorRisk && (
              <ColorRow
                label="Trailing actor risk color"
                value={settings.trailingActorRiskColor}
                onChange={(value) =>
                  setBackgroundPatternSettings({ trailingActorRiskColor: value })
                }
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection title="Trailing actor risk">
            {/* Confidence → dither-density map for the black bottom-anchored
                field. At each tile confidence = P(trailing actor reached ≥ that
                capability) from the CDF; these stops (linearly interpolated) map
                it to a dot density. Hover the graph to read the live confidence. */}
            <ConfidenceStopsEditor
              stops={settings.trailingActorRiskConfidenceStops}
              onChange={(stops) =>
                setBackgroundPatternSettings({ trailingActorRiskConfidenceStops: stops })
              }
            />
            {/* Which deal-survival "version" of the field to show. Auto follows
                the story date (recedes as the deal endures); overriding pins a
                chosen conditioning year (→ survival = year − deal-start). */}
            <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
              <input
                type="checkbox"
                checked={settings.trailingActorRiskConditioningYearOverride == null}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    trailingActorRiskConditioningYearOverride: event.target.checked
                      ? null
                      : 2035,
                  })
                }
              />
              <span>Version follows story year (auto)</span>
            </label>
            {settings.trailingActorRiskConditioningYearOverride != null && (
              <NumberRow
                label="Conditioning year"
                value={settings.trailingActorRiskConditioningYearOverride}
                min={2029}
                max={2052}
                step={1}
                format={(n) => n.toFixed(0)}
                onChange={(value) =>
                  setBackgroundPatternSettings({
                    trailingActorRiskConditioningYearOverride: Math.round(value),
                  })
                }
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection title="Pattern">
            <Field label="Noise source">
              <select
                value={settings.noiseSource}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    noiseSource: event.target.value as NoiseSource,
                  })
                }
                className="w-full bg-transparent border border-black/10 rounded px-1 py-0.5 font-mono text-[10px]"
              >
                {NOISE_SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Dither pattern">
              <select
                value={settings.ditherPattern}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    ditherPattern: event.target.value as DitherPattern,
                  })
                }
                className="w-full bg-transparent border border-black/10 rounded px-1 py-0.5 font-mono text-[10px]"
              >
                {DITHER_PATTERN_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </CollapsibleSection>
          <CollapsibleSection title="Gradient">
            {/* Loss-of-control (red) risk → dither-density map. Risk is the
                geometric position across the red band (0 = onset line, 1 =
                certain line); hover the graph to read it. These stops replace
                the fade/curve below for the red field's density. */}
            <ConfidenceStopsEditor
              stops={settings.redRiskConfidenceStops}
              onChange={(stops) =>
                setBackgroundPatternSettings({ redRiskConfidenceStops: stops })
              }
            />
            <NumberRow
              label="Gradient fade"
              value={settings.gradientStrength}
              min={0}
              max={1}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({ gradientStrength: value })
              }
            />
            <NumberRow
              label="Gradient curve"
              value={settings.gradientCurve}
              min={0.2}
              max={4}
              step={0.05}
              onChange={(value) =>
                setBackgroundPatternSettings({ gradientCurve: value })
              }
            />
            <NumberRow
              label="Gradient angle"
              value={settings.gradientAngle}
              min={0}
              max={90}
              step={1}
              onChange={(value) =>
                setBackgroundPatternSettings({ gradientAngle: value })
              }
              format={(n) => `${Math.round(n)}°`}
            />
            <NumberRow
              label="Dither density"
              value={settings.ditherDensity}
              min={0}
              max={2}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({ ditherDensity: value })
              }
            />
            <NumberRow
              label="Min dither density"
              value={settings.minDitherDensity}
              min={0}
              max={1}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({ minDitherDensity: value })
              }
            />
            <NumberRow
              label="Max dot density"
              value={settings.maxDitherDensity}
              min={0}
              max={1}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({ maxDitherDensity: value })
              }
            />
            <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
              <input
                type="checkbox"
                checked={settings.ditherFillToTop}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    ditherFillToTop: event.target.checked,
                  })
                }
              />
              <span>Fade to chart top (ignore upper risk line)</span>
            </label>
          </CollapsibleSection>
          <CollapsibleSection title="Bands">
            <NumberRow
              label="Density bands"
              value={settings.densityBands}
              min={0}
              max={16}
              step={1}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  densityBands: Math.round(value),
                })
              }
              format={(n) =>
                Math.round(n) < 2 ? 'Off' : `${Math.round(n)} bands`
              }
            />
            <NumberRow
              label="Band thickness ×"
              value={settings.bandThicknessMultiplier}
              min={0.5}
              max={2}
              step={0.05}
              onChange={(value) =>
                setBackgroundPatternSettings({ bandThicknessMultiplier: value })
              }
              format={(n) => `${n.toFixed(2)}×`}
            />
            <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
              <input
                type="checkbox"
                checked={settings.densityBandsHoverOnly}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    densityBandsHoverOnly: event.target.checked,
                  })
                }
              />
              <span>Bands on hover only</span>
            </label>
            <NumberRow
              label="Min band dot step"
              value={settings.minDotRadiusStepPx}
              min={0}
              max={2}
              step={0.05}
              onChange={(value) =>
                setBackgroundPatternSettings({ minDotRadiusStepPx: value })
              }
              format={(n) => (n <= 0 ? 'Off' : `${n.toFixed(2)}px`)}
            />
          </CollapsibleSection>
          <CollapsibleSection title="Band noise">
            <NumberRow
              label="Amount"
              value={settings.bandBoundaryNoise}
              min={0}
              max={1}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({ bandBoundaryNoise: value })
              }
              format={(n) => (n <= 0 ? 'Off' : n.toFixed(2))}
            />
            <Field label="Type">
              <select
                value={settings.bandBoundaryNoiseType}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    bandBoundaryNoiseType:
                      event.target.value as BandBoundaryNoiseType,
                  })
                }
                className="w-full bg-transparent border border-black/10 rounded px-1 py-0.5 font-mono text-[10px]"
              >
                {BAND_BOUNDARY_NOISE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <NumberRow
              label="X freq"
              value={settings.bandBoundaryNoiseFrequencyX}
              min={0.001}
              max={4}
              step={0.001}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  bandBoundaryNoiseFrequencyX: value,
                })
              }
              format={(n) => n.toFixed(3)}
            />
            <NumberRow
              label="Y freq"
              value={settings.bandBoundaryNoiseFrequencyY}
              min={0.001}
              max={4}
              step={0.001}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  bandBoundaryNoiseFrequencyY: value,
                })
              }
              format={(n) => n.toFixed(3)}
            />
            <NumberRow
              label="Octaves"
              value={settings.bandBoundaryNoiseOctaves}
              min={1}
              max={6}
              step={1}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  bandBoundaryNoiseOctaves: Math.round(value),
                })
              }
              format={(n) => String(Math.round(n))}
            />
            <NumberRow
              label="Quantize steps"
              value={settings.bandBoundaryNoiseQuantizeSteps}
              min={0}
              max={16}
              step={1}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  bandBoundaryNoiseQuantizeSteps: Math.round(value),
                })
              }
              format={(n) =>
                Math.round(n) < 2 ? 'Off' : `${Math.round(n)} steps`
              }
            />
          </CollapsibleSection>
          <CollapsibleSection title="Hover highlight">
            <NumberRow
              label="Band edge softness"
              value={settings.bandHighlightEdgeSoftness}
              min={0}
              max={1}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({ bandHighlightEdgeSoftness: value })
              }
            />
            <NumberRow
              label="Hover dot radius ×"
              value={settings.bandHighlightDotRadiusMultiplier}
              min={0}
              max={4}
              step={0.05}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  bandHighlightDotRadiusMultiplier: value,
                })
              }
              format={(n) => `${n.toFixed(2)}×`}
            />
            <NumberRow
              label="Hover dot radius +"
              value={settings.bandHighlightDotRadiusAdditivePx}
              min={0}
              max={8}
              step={0.05}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  bandHighlightDotRadiusAdditivePx: value,
                })
              }
              format={(n) => (n <= 0 ? 'Off' : `+${n.toFixed(2)}px`)}
            />
            <NumberRow
              label="Hover brightness"
              value={settings.bandHighlightBrightnessMultiplier}
              min={0}
              max={2}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  bandHighlightBrightnessMultiplier: value,
                })
              }
              format={(n) => `${n.toFixed(2)}×`}
            />
            <NumberRow
              label="Hover saturation"
              value={settings.bandHighlightSaturationMultiplier}
              min={0}
              max={2}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  bandHighlightSaturationMultiplier: value,
                })
              }
              format={(n) => `${n.toFixed(2)}×`}
            />
          </CollapsibleSection>
          <CollapsibleSection title="LoC line">
            <NumberRow
              label="LOC line offset"
              value={settings.lossOfControlLineYOffset}
              min={-80}
              max={80}
              step={1}
              onChange={(value) =>
                setBackgroundPatternSettings({ lossOfControlLineYOffset: value })
              }
              format={(n) => `${n > 0 ? '+' : ''}${Math.round(n)}px`}
            />
            <NumberRow
              label="LOC strip opacity"
              value={settings.lossOfControlGradientStripOpacity}
              min={0}
              max={1}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({
                  lossOfControlGradientStripOpacity: value,
                })
              }
            />
          </CollapsibleSection>
          <CollapsibleSection title="Capability line">
            <NumberRow
              label="Line smoothing"
              value={settings.capabilityLineSmoothing}
              min={0}
              max={1}
              step={0.01}
              onChange={(value) =>
                setBackgroundPatternSettings({ capabilityLineSmoothing: value })
              }
              format={(n) => (n <= 0 ? 'Off' : n.toFixed(2))}
            />
          </CollapsibleSection>
          {showNoiseControls && (
            <CollapsibleSection title="Noise tuning">
              <NumberRow
                label="Density (frequency)"
                value={settings.baseFrequency}
                min={0.01}
                max={2}
                step={0.01}
                onChange={(value) =>
                  setBackgroundPatternSettings({ baseFrequency: value })
                }
              />
              <NumberRow
                label="Detail (octaves)"
                value={settings.numOctaves}
                min={1}
                max={6}
                step={1}
                onChange={(value) => setBackgroundPatternSettings({ numOctaves: value })}
                format={(n) => String(Math.round(n))}
              />
              <NumberRow
                label="Noise amplitude"
                value={settings.noiseAmplitude}
                min={0}
                max={3}
                step={0.05}
                onChange={(value) =>
                  setBackgroundPatternSettings({ noiseAmplitude: value })
                }
              />
              <NumberRow
                label="Noise offset"
                value={settings.noiseOffset}
                min={-1}
                max={1}
                step={0.01}
                onChange={(value) =>
                  setBackgroundPatternSettings({ noiseOffset: value })
                }
              />
              <NumberRow
                label="Seed"
                value={settings.seed}
                min={0}
                max={50}
                step={1}
                onChange={(value) =>
                  setBackgroundPatternSettings({ seed: Math.round(value) })
                }
                format={(n) => String(Math.round(n))}
              />
            </CollapsibleSection>
          )}
          {showCellSizeControl && (
            <CollapsibleSection title="Dither tuning">
              <NumberRow
                label="Cell size (px)"
                value={settings.patternScale}
                min={1}
                max={32}
                step={1}
                onChange={(value) =>
                  setBackgroundPatternSettings({
                    patternScale: Math.round(value),
                  })
                }
                format={(n) => `${Math.round(n)}px`}
              />
              <Field label="Dot shape">
                <select
                  value={settings.dotShape}
                  onChange={(event) =>
                    setBackgroundPatternSettings({
                      dotShape: event.target.value as DotShape,
                    })
                  }
                  className="w-full bg-transparent border border-black/10 rounded px-1 py-0.5 font-mono text-[10px]"
                >
                  {DOT_SHAPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <NumberRow
                label="Dither X offset"
                value={settings.ditherPatternOffsetX}
                min={-32}
                max={32}
                step={0.1}
                onChange={(value) =>
                  setBackgroundPatternSettings({ ditherPatternOffsetX: value })
                }
                format={(n) => `${n > 0 ? '+' : ''}${n.toFixed(1)}px`}
              />
              {settings.ditherPattern === 'dots' && (
                <>
                  <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
                    <input
                      type="checkbox"
                      checked={settings.globeDotExclusionEnabled}
                      onChange={(event) =>
                        setBackgroundPatternSettings({
                          globeDotExclusionEnabled: event.target.checked,
                        })
                      }
                    />
                    <span>Skip dots under globe</span>
                  </label>
                  <NumberRow
                    label="Globe dot gap"
                    value={settings.globeDotExclusionPaddingPx}
                    min={0}
                    max={16}
                    step={0.25}
                    onChange={(value) =>
                      setBackgroundPatternSettings({
                        globeDotExclusionPaddingPx: value,
                      })
                    }
                    format={(n) => `${n.toFixed(2)}px`}
                  />
                  <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
                    <input
                      type="checkbox"
                      checked={settings.capabilityLineDotExclusionEnabled}
                      onChange={(event) =>
                        setBackgroundPatternSettings({
                          capabilityLineDotExclusionEnabled: event.target.checked,
                        })
                      }
                    />
                    <span>Shrink dots under capability line</span>
                  </label>
                  <NumberRow
                    label="Capability line gap"
                    value={settings.capabilityLineDotExclusionPaddingPx}
                    min={0}
                    max={16}
                    step={0.25}
                    onChange={(value) =>
                      setBackgroundPatternSettings({
                        capabilityLineDotExclusionPaddingPx: value,
                      })
                    }
                    format={(n) => `${n.toFixed(2)}px`}
                  />
                  <NumberRow
                    label="Capability dot size"
                    value={settings.capabilityLineDotRadiusMultiplier}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={(value) =>
                      setBackgroundPatternSettings({
                        capabilityLineDotRadiusMultiplier: value,
                      })
                    }
                    format={(n) => n.toFixed(2)}
                  />
                </>
              )}
            </CollapsibleSection>
          )}
          <CollapsibleSection title="Capability line">
            <ColorRow
              label="Solid (past) color"
              value={settings.capabilityLineSolidColor}
              onChange={(value) =>
                setBackgroundPatternSettings({ capabilityLineSolidColor: value })
              }
            />
            <ColorRow
              label="Dashed (future) color"
              value={settings.capabilityLineDashedColor}
              onChange={(value) =>
                setBackgroundPatternSettings({ capabilityLineDashedColor: value })
              }
            />
            <NumberRow
              label="Line thickness"
              value={settings.capabilityLineStrokeWidth}
              min={0}
              max={12}
              step={0.5}
              onChange={(value) =>
                setBackgroundPatternSettings({ capabilityLineStrokeWidth: value })
              }
              format={(n) => `${n.toFixed(1)}px`}
            />
            <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
              <input
                type="checkbox"
                checked={settings.capabilityFutureLineDashed}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    capabilityFutureLineDashed: event.target.checked,
                  })
                }
              />
              <span>Dash the future line</span>
            </label>
            <NumberRow
              label="Future line thickness"
              value={settings.capabilityFutureLineStrokeWidth}
              min={0}
              max={12}
              step={0.5}
              onChange={(value) =>
                setBackgroundPatternSettings({ capabilityFutureLineStrokeWidth: value })
              }
              format={(n) => `${n.toFixed(1)}px`}
            />
            {settings.capabilityFutureLineDashed && (
              <NumberRow
                label="Future dash length"
                value={settings.capabilityFutureLineDashLength}
                min={0.5}
                max={20}
                step={0.5}
                onChange={(value) =>
                  setBackgroundPatternSettings({ capabilityFutureLineDashLength: value })
                }
                format={(n) => `${n.toFixed(1)}px`}
              />
            )}
            <NumberRow
              label="“AI Capabilities” label size"
              value={settings.aiCapabilitiesLabelFontSize}
              min={4}
              max={32}
              step={0.5}
              onChange={(value) =>
                setBackgroundPatternSettings({ aiCapabilitiesLabelFontSize: value })
              }
              format={(n) => `${n.toFixed(1)}px`}
            />
            <label className="flex items-center gap-2 font-mono text-[10px] text-black/70">
              <input
                type="checkbox"
                checked={settings.capabilityRiskSplitEnabled}
                onChange={(event) =>
                  setBackgroundPatternSettings({
                    capabilityRiskSplitEnabled: event.target.checked,
                  })
                }
              />
              <span>Red dither below/right of line</span>
            </label>
            {settings.capabilityRiskSplitEnabled && (
              <>
                <ColorRow
                  label="Safe-side color"
                  value={settings.capabilitySafeSideColor}
                  onChange={(value) =>
                    setBackgroundPatternSettings({ capabilitySafeSideColor: value })
                  }
                />
                <NumberRow
                  label="Safe-side dither density"
                  value={settings.safeSideDitherDensity}
                  min={0}
                  max={2}
                  step={0.01}
                  onChange={(value) =>
                    setBackgroundPatternSettings({ safeSideDitherDensity: value })
                  }
                />
                <NumberRow
                  label="Safe-side min density"
                  value={settings.safeSideMinDitherDensity}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(value) =>
                    setBackgroundPatternSettings({ safeSideMinDitherDensity: value })
                  }
                />
                <NumberRow
                  label="Safe-side max dot density"
                  value={settings.safeSideMaxDitherDensity}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(value) =>
                    setBackgroundPatternSettings({ safeSideMaxDitherDensity: value })
                  }
                />
              </>
            )}
          </CollapsibleSection>
          </div>
          <button
            type="button"
            onClick={handleResetToDefaults}
            className="mt-1 w-full text-[9px] uppercase tracking-wider text-black/60 border border-black/10 rounded py-1 hover:bg-black/5"
          >
            Reset to defaults ({DEFAULT_BACKGROUND_PATTERN_SETTINGS.color})
          </button>
        </div>
      )}
    </div>
  )
}
